'use client'

import {useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject} from 'react'
import {autoUpdate, computePosition, flip, offset, shift, size, type Placement} from '@floating-ui/dom'

/** Portaled lists use Floating UI's visual viewport boundary, including OSK/zoom. */
export function useFloatingPosition(
    triggerRef: RefObject<HTMLElement | null>,
    dropdownRef: RefObject<HTMLElement | null>,
    placement: Placement = 'bottom',
    sideOffset = 4,
    matchTriggerWidth = false,
    mounted = false,
    open = false,
    zIndex = 110,
) {
    const [position, setPosition] = useState<CSSProperties>({position: 'fixed', visibility: 'hidden', zIndex})
    const [isPositioned, setIsPositioned] = useState(false)
    const [resolvedPlacement, setResolvedPlacement] = useState<Placement>(placement)
    const revision = useRef(0)

    const updatePosition = useCallback(() => {
        const reference = triggerRef.current
        const floating = dropdownRef.current
        if (!reference || !floating || !open) return
        const currentRevision = ++revision.current
        void computePosition(reference, floating, {
            strategy: 'fixed', placement,
            // Side placements may have no room on either side of a narrow calendar cell.
            // Shift both axes so the panel stays inside the viewport even after flipping.
            middleware: [offset(sideOffset), flip({padding: 8}), shift({padding: 8, crossAxis: true}), size({
                padding: 8,
                apply({availableWidth, availableHeight, rects, elements}) {
                    if (revision.current !== currentRevision) return
                    Object.assign(elements.floating.style, {
                        maxWidth: `${Math.max(0, availableWidth)}px`,
                        maxHeight: `${Math.max(0, availableHeight)}px`,
                        width: matchTriggerWidth ? `${Math.min(rects.reference.width, Math.max(0, availableWidth))}px` : '',
                    })
                },
            })],
        }).then(({x, y, placement: resolved}) => {
            if (revision.current !== currentRevision || dropdownRef.current !== floating) return
            setPosition({position: 'fixed', visibility: 'visible', left: x, top: y, zIndex})
            setResolvedPlacement(resolved)
            setIsPositioned(true)
        })
    }, [triggerRef, dropdownRef, open, placement, sideOffset, matchTriggerWidth, zIndex])

    useLayoutEffect(() => {
        if (!mounted || !open) {
            if (!mounted) {
                setIsPositioned(false)
                setPosition({position: 'fixed', visibility: 'hidden', zIndex})
            }
            return
        }
        const reference = triggerRef.current
        const floating = dropdownRef.current
        if (!reference || !floating) return
        let frame = 0
        let initial = true
        const scheduleUpdate = () => {
            if (initial) {
                initial = false
                updatePosition()
                return
            }
            // Size middleware can resize an observed list. Apply subsequent
            // updates outside ResizeObserver delivery, coalesced into one frame.
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(updatePosition)
        }
        const stop = autoUpdate(reference, floating, scheduleUpdate, {
            elementResize: typeof ResizeObserver !== 'undefined',
            layoutShift: typeof IntersectionObserver !== 'undefined',
        })
        return () => { ++revision.current; cancelAnimationFrame(frame); stop() }
    }, [mounted, open, triggerRef, dropdownRef, updatePosition, zIndex])

    return {position, isPositioned, resolvedPlacement, updatePosition}
}
